import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from '../state/GlobalContext';
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Textarea,
  RadioGroup,
  Radio,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import styled from '@emotion/styled';
import useForm from '../../utils/useForm';
import { formatFrequency } from '../utils';

type reactEvent = React.ChangeEvent<
  HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
>;

interface IProps {
  onCancel: () => void;
  onSubmit: (
    shippingCode: string | undefined,
    localDeliveryOptions: {
      phone?: string | undefined;
      instrumentation?: string | undefined;
    },
  ) => void;
  isPhoneRequired: boolean;
  shippingOptions?: ShippingOptionsResponse;
  subscription: Subscription;
  loading: boolean;
}

export default ({
  onCancel,
  onSubmit,
  shippingOptions,
  loading,
  isPhoneRequired,
  subscription,
}: IProps): React.ReactElement => {
  const initialLocalDeliveryOptions =
    subscription.DeliveryMethod?.localDeliveryOption;
  const { values: localDeliveryOptions, handleChange } = useForm<{
    phone: string | undefined;
    instructions: string | undefined;
  }>({
    initialValues: {
      phone: initialLocalDeliveryOptions?.phone || '',
      instructions: initialLocalDeliveryOptions?.instructions || '',
    },
  });

  const { shopEmail } = useGlobalContext();
  const { t } = useTranslation();
  const [selected, setSelected] = useState(
    shippingOptions?.deliveryOptions?.deliveryOptions?.[0]?.code,
  );
  const handleSubmit = () => {
    onSubmit(selected, localDeliveryOptions);
  };

  if (!selected) {
    return (
      <>
        <ModalHeader>{t('subscription.update_shipping')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <p>{t('subscription.update_shipping_error', { shopEmail })}</p>
        </ModalBody>
        <ModalFooter>
          <Button mr="4" onClick={onCancel} variant="outlinedBig">
            {t('titles.cancel')}
          </Button>
        </ModalFooter>
      </>
    );
  }

  return (
    <>
      <ModalHeader>{t('subscription.update_shipping')}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <VStack spacing={2} align="stretch" id="shipping_options_form">
          <RadioGroup value={selected} onChange={setSelected}>
            <Stack spacing={2}>
              {shippingOptions?.deliveryOptions?.deliveryOptions
                ?.filter(
                  ({ __typename }) => __typename !== 'SubscriptionPickupOption',
                )
                .map((shippingOption, idx) => (
                  <Flex
                    key={idx}
                    direction="column"
                    w="100%"
                    className="bundleapp-flex bundleapp-flex-align-center"
                  >
                    <Radio
                      value={shippingOption.code}
                      className="bundleapp-flex bundleapp-flex-align-center"
                      size="md"
                    >
                      <Flex direction="column" ml={2}>
                        <Text fontWeight="bold">
                          {shippingOption.presentmentTitle} -{' '}
                          <span>{shippingOption.formattedPrice}</span>
                        </Text>
                        <Text>
                          {formatFrequency(
                            t,
                            subscription.DeliveryIntervalDeliveryCount,
                            subscription.FormattedFrequency,
                          )}
                        </Text>
                        {shippingOption['__typename'] ===
                          'SubscriptionLocalDeliveryOption' &&
                          shippingOption.description && (
                            <Text>{shippingOption.description}</Text>
                          )}
                      </Flex>
                    </Radio>
                    {shippingOption['__typename'] ===
                      'SubscriptionLocalDeliveryOption' &&
                      selected === shippingOption.code && (
                        <LocalDeliveryInformation
                          handleChange={handleChange}
                          values={localDeliveryOptions}
                        />
                      )}
                  </Flex>
                ))}
            </Stack>
          </RadioGroup>
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Button mr="4" onClick={onCancel} variant="outlinedBig">
          {isPhoneRequired ? t('titles.back') : t('titles.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          isLoading={loading}
          disabled={loading}
          variant="blackBig"
        >
          {t('titles.save')}
        </Button>
      </ModalFooter>
    </>
  );
};

const LocalDeliveryInformation = ({
  handleChange,
  values,
}: {
  handleChange: (e: reactEvent) => void;
  values: {
    phone: string | undefined;
    instructions: string | undefined;
  };
}) => {
  const { t } = useTranslation();

  return (
    <LocalDeliveryInformationContainer className="bundleapp-formcontainer">
      <Flex className="phone" mb="6">
        <FormControl id="phone">
          <Input
            name="phone"
            onChange={handleChange}
            placeholder={t('new_payment_method.phone')}
            type="tel"
            value={values.phone}
            required
          />
          <FormLabel>{t('new_payment_method.phone')}</FormLabel>
        </FormControl>
      </Flex>
      <Flex className="instructions" mb="6">
        <FormControl id="instructions">
          <Textarea
            name="instructions"
            onChange={handleChange}
            placeholder={t('new_payment_method.instructions')}
            value={values.instructions}
          />
          <FormLabel>{t('new_payment_method.instructions')}</FormLabel>
        </FormControl>
      </Flex>
    </LocalDeliveryInformationContainer>
  );
};

const LocalDeliveryInformationContainer = styled.form`
  display: flex;
  width: 100%;
  max-width: 100%;
  flex-direction: column;
  gap: 2rem;
  flex-grow: 1;
  justify-content: flex-start;
  align-items: stretch;
  padding-block-start: 2rem;
  > div {
    flex: 1 1 100%;
  }

  .phone {
    max-width: 60%;
  }

  textarea {
    min-width: 0px;
    min-height: 10rem;
    outline: 2px solid transparent;
    outline-offset: 2px;
    position: relative;
    appearance: none;
    transition-property: var(--chakra-transition-property-common);
    transition-duration: var(--chakra-transition-duration-normal);
    font-size: var(--chakra-fontSizes-md);
    padding-inline-start: var(--chakra-space-4);
    padding-inline-end: var(--chakra-space-4);
    border-radius: 8px;
    background: var(--chakra-colors-gray-50);
    border: var(--chakra-borders-none);
    color: var(--chakra-colors-gray-800);
    padding-top: var(--chakra-space-2);
    padding-bottom: var(--chakra-space-2);
  }
`;
